import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  FormActions,
  FormGrid,
  NumberField,
  Pill,
  SelectField,
  TextField,
  fmtDataISO,
} from "@atlas/ui/web";
import { atlas } from "../../../../lib/sdk";
import type { BancoTabelaInput } from "@atlas/sdk";
import { ApiHttpError } from "@atlas/sdk";
import { CET_MINIMO_AM } from "@atlas/domain";

/** CET minimo em percentual, como o campo trabalha (spec 014). */
const CET_MINIMO_PCT = Math.round(CET_MINIMO_AM * 10000) / 100;

export function BancoTabelaEmprestimosForm() {
  const params = useParams<{ id?: string }>();
  const isNovo = params.id === "novo" || !params.id;
  const id = isNovo ? undefined : params.id;
  const nav = useNavigate();
  const qc = useQueryClient();

  const convenios = useQuery({ queryKey: ["banco", "convenios"], queryFn: () => atlas.banco.convenios() });
  const existing = useQuery({
    queryKey: ["banco", "tabela", id],
    queryFn: () => atlas.banco.getTabela(id!),
    enabled: !!id,
    // Nunca serve cache stale no form de edicao — precisa refletir o que
    // foi salvo na ultima passagem, senao usuario ve o valor antigo.
    staleTime: 0,
    refetchOnMount: "always",
    // Desabilita refetch em foco: se o usuario Alt-Tab enquanto edita
    // partial, o refetch poderia sobrescrever suas alteracoes ainda nao
    // salvas via o useEffect que sincroniza data -> state.
    refetchOnWindowFocus: false,
  });

  const [convenioId, setConvenioId] = useState("");
  const [nome, setNome] = useState("");
  const [taxaPct, setTaxaPct] = useState(1.79);
  // Faixa de prazo do produto (20/08/2026). Uma tabela cobre de prazoMin ate
  // prazoMax; e' o que permite ao banco cobrar 3% ate 30x e 5% de 31x a 90x no
  // mesmo convenio, em vez de uma taxa unica pra tudo.
  const [prazoMinMeses, setPrazoMinMeses] = useState(1);
  const [prazoMaxMeses, setPrazoMaxMeses] = useState(120);
  const [vigenciaInicio, setVigenciaInicio] = useState(() => new Date().toISOString().slice(0, 10));
  const [vigenciaFim, setVigenciaFim] = useState("");
  const [ativo, setAtivo] = useState(true);

  /**
   * Hidrata o formulario a partir da tabela salva — UMA vez por tabela.
   *
   * Antes o efeito dependia tambem de `convenios.data` e de `convenioId`, e a
   * cada execucao reaplicava TODOS os campos. A query de convenios usa os
   * defaults do app (staleTime 30s + refetch ao voltar o foco da janela), entao
   * bastava sair e voltar pra aba — ou qualquer invalidacao da lista — pra o
   * efeito rodar de novo e devolver os campos aos valores salvos, apagando o
   * que o operador tinha acabado de digitar.
   *
   * O sintoma nao parecia um reset: parecia campo travado. A data aceitava a
   * edicao e voltava sozinha, entao a leitura natural era "esse campo nao deixa
   * editar". A protecao que ja existia (refetchOnWindowFocus: false na query da
   * tabela) nao cobria nada, porque a re-execucao vinha da OUTRA query.
   * Cliente 11/08/2026, editando a vigencia da TBL-001.
   */
  const hidratadoDe = useRef<string | null>(null);
  useEffect(() => {
    if (existing.data) {
      const t = existing.data.tabela;
      if (hidratadoDe.current === t.id) return;
      hidratadoDe.current = t.id;
      setConvenioId(t.convenioId);
      // Migracao suave: taxa unica (taxaAm) preferida; se tabela legada so
      // tem taxaMinAm/taxaMaxAm, usa a max (que era o topo praticado) e
      // proximo save ja persiste como unica.
      const raw = (t as { taxaAm?: number; taxaMaxAm?: number; taxaMinAm?: number });
      // Arredonda 4 casas: a taxa e' guardada como FRACAO (0.0156) e o *100 de
      // ponto flutuante devolve 1.5599999999999998, que aparecia cru no campo.
      // Mesmo tratamento que a tela de propostas ja fazia desde 30/07/2026 —
      // aqui tinha ficado de fora. Cliente 11/08/2026, editando a TBL-002.
      setTaxaPct(Number((((raw.taxaAm ?? raw.taxaMaxAm ?? raw.taxaMinAm ?? 0.0179)) * 100).toFixed(4)));
      setNome(t.nome ?? "");
      // Tabela anterior a 20/08/2026 nao tem inicio de faixa: sempre significou
      // "de 1x ate o teto", e e' assim que ela reabre.
      setPrazoMinMeses(t.prazoMinMeses ?? 1);
      setPrazoMaxMeses(t.prazoMaxMeses);
      setVigenciaInicio(t.vigenciaInicio);
      setVigenciaFim(t.vigenciaFim ?? "");
      setAtivo(t.ativo);
    }
  }, [existing.data]);

  // Tabela NOVA nasce no convenio ATIVO — o que esta selecionado no topo.
  // Antes assumia o primeiro da lista: com Guarulhos selecionado, o form
  // abria em Capistrano e o painel de regras mostrava os limites da
  // prefeitura errada. Cai no primeiro so se nao houver ativo definido.
  // Cliente 11/08/2026.
  useEffect(() => {
    if (!isNovo || convenioId || !convenios.data) return;
    setConvenioId(convenios.data.activeId ?? convenios.data.convenios[0]?.id ?? "");
  }, [isNovo, convenioId, convenios.data]);

  // Trocar de prefeitura no topo enquanto se cria uma tabela move o formulario
  // junto — sem isso o form continuaria no convenio anterior, gravando a
  // tabela na prefeitura que o operador acabou de sair. So na CRIACAO: tabela
  // existente pertence ao convenio dela e nao acompanha a troca.
  const activeId = convenios.data?.activeId;
  useEffect(() => {
    if (!isNovo || !activeId || convenioId === activeId) return;
    setConvenioId(activeId);
  }, [isNovo, activeId, convenioId]);

  const convenioSelecionado = useMemo(() => convenios.data?.convenios.find((c) => c.id === convenioId), [convenios.data, convenioId]);
  const convenioNome = convenioSelecionado?.nome ?? "";
  /** Teto de parcelas definido pela averbadora/prefeitura pro convenio selecionado.
   *  Banco nao pode ultrapassar — dropdown mostra 1..teto e backend bloqueia
   *  com 422 se alguem burlar o UI. Cliente pediu 30/07/2026 que a lista
   *  deixe de ser fixa (12,24,...) e seja 1..N. */
  const tetoPrefeitura = convenioSelecionado?.maxParcelas ?? 120;
  const opcoesPrazo = useMemo(
    () => Array.from({ length: Math.max(1, Math.floor(tetoPrefeitura)) }, (_, i) => i + 1),
    [tetoPrefeitura],
  );
  // Se o convenio mudou e o valor atual excede o teto novo, reduz automaticamente.
  useEffect(() => {
    if (prazoMaxMeses > tetoPrefeitura) setPrazoMaxMeses(tetoPrefeitura);
  }, [tetoPrefeitura, prazoMaxMeses]);
  // (Aqui existiam um preenchimento automatico da vigencia fim e uma
  // sincronizacao dela na troca de convenio. Os dois sairam junto com a edicao
  // do campo: a vigencia agora e' DERIVADA do convenio — ver
  // vigenciaInicioFinal/vigenciaFimFinal —, entao nao ha estado pra manter em
  // dia, e o bug que eles tentavam remendar deixou de existir. Trocar de
  // convenio troca a vigencia exibida sozinho.)
  const trocarConvenio = (novoId: string) => setConvenioId(novoId);
  // Teto de CET da prefeitura (spec 014, 29/09/2026 — antes era de juros; o
  // numero da tabela passou a ser o CET). De 28/07/2026 a 20/08/2026 ele nao
  // limitava nada aqui: salvar a tabela reescrevia o proprio teto com o valor
  // digitado (e a taxa das outras tabelas do convenio junto). Agora ele recusa
  // — no backend com 422, e aqui travando o botao antes da viagem.
  const taxaTetoPct = convenioSelecionado?.taxaMaxAm ?? null;
  const vigConvInicio = convenioSelecionado?.vigenciaInicio ?? null;
  const vigConvFim = convenioSelecionado?.vigenciaFim ?? null;
  // Tolerancia de 1e-6: o campo trabalha em percentual e o backend em fracao,
  // e o ida-e-volta introduz ruido na ultima casa.
  const taxaAcimaTeto = taxaTetoPct != null && taxaTetoPct > 0 && taxaPct > taxaTetoPct + 1e-6;
  // CET minimo (spec 014): 0,5% a.m. Protege contra "0,179" digitado no lugar
  // de "1,79". O backend recusa com a mesma regra.
  const taxaAbaixoMinimo = taxaPct > 0 && taxaPct < CET_MINIMO_PCT - 1e-6;
  const faixaInvertida = prazoMinMeses > prazoMaxMeses;
  /**
   * Vigencia efetiva da tabela = a do convenio. Nao ha estado editavel: o
   * valor exibido e o gravado saem daqui, entao nao existe divergencia
   * possivel entre o que a tela mostra e o que o backend aceita.
   *
   * Fallback pro valor salvo quando o convenio nao esta na lista deste banco
   * (tabela de convenio encerrado — ver o campo Convenio acima): sem convenio
   * pra espelhar, preserva o que ja estava gravado em vez de zerar.
   */
  const vigenciaInicioFinal = convenioSelecionado ? (vigConvInicio ?? vigenciaInicio) : vigenciaInicio;
  const vigenciaFimFinal = convenioSelecionado ? (vigConvFim ?? "") : vigenciaFim;

  const save = useMutation({
    mutationFn: () => {
      const body: BancoTabelaInput = {
        id,
        convenioId,
        convenio: convenioNome,
        taxaAm: Number((taxaPct / 100).toFixed(6)),
        nome: nome.trim() || undefined,
        prazoMinMeses,
        prazoMaxMeses,
        vigenciaInicio: vigenciaInicioFinal,
        vigenciaFim: vigenciaFimFinal || undefined,
        ativo,
      };
      return atlas.banco.upsertTabela(body);
    },
    onSuccess: () => {
      // Invalida BOTH: a lista ('tabelas') e o item individual ('tabela', id).
      // Sem invalidar o singular, ao reabrir o form o React Query retornava
      // o cache stale de antes do save — usuario via valor antigo.
      qc.invalidateQueries({ queryKey: ["banco", "tabelas"] });
      if (id) qc.invalidateQueries({ queryKey: ["banco", "tabela", id] });
      // Marketplace do servidor tambem le tabelas (via ofertas): invalidar
      // pra que o servidor veja imediatamente a nova taxa/prazo.
      qc.invalidateQueries({ queryKey: ["servidor", "ofertas"] });
      nav("/banco/cadastros/tabela-emprestimos");
    },
  });

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate();
      }}
      style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 840 }}
    >
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Cadastros • Tabela de Empréstimos
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>{id ? `Editar ${id}` : "Nova tabela"}</h1>
      </header>

      <section
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <FormGrid cols={2}>
          {/* EDICAO: convenio travado. A tabela ja pertence a uma prefeitura;
              oferecer outra no dropdown so abria porta pra engano — trocar ali
              moveria a tabela de prefeitura, coisa que ninguem quer fazer sem
              perceber. Foi tambem o que deixou a vigencia fim de um convenio
              vazar pra tabela de outro. Cliente 11/08/2026.
              Na CRIACAO segue escolhendo, que e' quando faz sentido. */}
          {isNovo ? (
            <SelectField
              label="Convênio"
              value={convenioId}
              onChange={(e) => trocarConvenio(e.target.value)}
              options={(convenios.data?.convenios ?? []).map((c) => ({ value: c.id, label: c.nome }))}
              required
            />
          ) : (
            <TextField
              label="Convênio"
              // Cai no nome gravado na propria tabela quando o convenio nao
              // esta mais na lista do banco (convenio encerrado, por exemplo)
              // — melhor mostrar de quem e' a tabela do que um traco.
              value={convenioNome || existing.data?.tabela.convenio || "—"}
              readOnly
              hint="Definido na criação da tabela — para outra prefeitura, crie uma tabela nova."
            />
          )}
          <SelectField
            label="Situação"
            value={ativo ? "1" : "0"}
            onChange={(e) => setAtivo(e.target.value === "1")}
            options={[
              { value: "1", label: "Ativo" },
              { value: "0", label: "Inativo" },
            ]}
          />
          {/* Nome comercial: com varias tabelas por convenio, "TBL-1-101" e
              "TBL-1-102" nao dizem nada nem pro operador nem pro servidor. */}
          <TextField
            label="Nome da tabela"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            maxLength={60}
            placeholder="Ex.: Curto, Estendido, Campanha servidor ativo"
            hint="Como esta tabela aparece pra você e pro servidor na comparação de ofertas."
          />
          <NumberField
            label="CET (% a.m.)"
            step={0.01}
            value={taxaPct}
            onChange={(e) => setTaxaPct(Number(e.target.value))}
            required
            error={
              taxaAcimaTeto
                ? `CET acima do teto de ${taxaTetoPct}% a.m. definido pela prefeitura.`
                : taxaAbaixoMinimo
                  ? `CET abaixo do mínimo de ${CET_MINIMO_PCT}% a.m. Confira a digitação (1,79 para 1,79%).`
                  : undefined
            }
            hint={`Custo efetivo total mensal: juros, IOF, tarifas e seguro. É o CET máximo desta faixa — o contrato pode sair abaixo, nunca acima.${taxaTetoPct != null && taxaTetoPct > 0 ? ` Teto do convênio: ${taxaTetoPct}% a.m.` : ""}`}
          />
          {/* Faixa de prazo. Antes so existia o teto ("prazo max"), e duas
              tabelas do mesmo convenio nao tinham como se distinguir — nem
              havia como saber qual valia pra 20x. Cliente 20/08/2026. */}
          <SelectField
            label="Prazo de (meses)"
            value={String(prazoMinMeses)}
            onChange={(e) => setPrazoMinMeses(Number(e.target.value))}
            options={opcoesPrazo.map((n) => ({ value: String(n), label: `${n} meses` }))}
            error={faixaInvertida ? "O prazo inicial não pode ser maior que o final." : undefined}
            hint="Primeira parcela coberta por esta tabela."
            required
          />
          <SelectField
            label="Prazo até (meses)"
            value={String(prazoMaxMeses)}
            onChange={(e) => setPrazoMaxMeses(Number(e.target.value))}
            options={opcoesPrazo.map((n) => ({ value: String(n), label: `${n} meses` }))}
            hint={`Última parcela coberta. Teto da prefeitura: ${tetoPrefeitura} meses.`}
            required
          />
          {/* Vigencia e' do CONVENIO — leitura. O banco edita taxa e prazo.
              Antes eram campos de data livres, limitados a "caber dentro" do
              convenio: dava pra salvar inicio 03/01 num convenio que comeca
              01/01, e fim 01/01/2027 num convenio ABERTO. O operador ficava
              sem saber de onde a data tinha vindo, porque nao vinha de regra
              nenhuma. Cliente 11/08/2026. */}
          <TextField
            label="Vigência início"
            value={vigenciaInicioFinal ? fmtDataISO(vigenciaInicioFinal) : "—"}
            readOnly
            hint="Definida pelo convênio — mesma data para todas as tabelas dele."
          />
          <TextField
            label="Vigência fim"
            value={vigenciaFimFinal ? fmtDataISO(vigenciaFimFinal) : "Aberta (sem data de fim)"}
            readOnly
            hint="Definida pelo convênio. Para alterar, fale com a averbadora."
          />
        </FormGrid>
      </section>

      {convenioSelecionado && (convenioSelecionado.taxaMaxAm != null || convenioSelecionado.vinculosAceitos.length > 0 || convenioSelecionado.regrasEspeciais) ? (
        <section
          style={{
            background: "color-mix(in srgb, var(--gold-500) 6%, var(--bg-elev))",
            border: "1px solid color-mix(in srgb, var(--gold-500) 40%, var(--border))",
            borderRadius: 12,
            padding: 20,
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 11, letterSpacing: "0.1em", fontWeight: 700, color: "var(--gold-500)", textTransform: "uppercase" }}>
                Regras do convênio
              </div>
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>
                Definidas pela averbadora + prefeitura. Somente leitura — sua tabela precisa respeitar esses limites.
              </div>
            </div>
            {convenioSelecionado.configAtivo != null ? (
              <Pill variant={convenioSelecionado.configAtivo ? "averbado" : "expirado"}>
                {convenioSelecionado.configAtivo ? "Vigente" : "Suspenso"}
              </Pill>
            ) : null}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: 10,
              fontSize: 13,
            }}
          >
            {convenioSelecionado.taxaMaxAm != null ? (
              <RegraCard label="Teto de CET a.m." value={`${convenioSelecionado.taxaMaxAm}%`} />
            ) : null}
            <RegraCard label="Máx. parcelas" value={String(convenioSelecionado.maxParcelas)} />
            {convenioSelecionado.maxComprometimentoPct != null ? (
              <RegraCard label="Máx. comprometimento" value={`${Math.round(convenioSelecionado.maxComprometimentoPct * 100)}%`} />
            ) : null}
            {convenioSelecionado.idadeMin != null && convenioSelecionado.idadeMax != null ? (
              <RegraCard label="Faixa etária" value={`${convenioSelecionado.idadeMin}–${convenioSelecionado.idadeMax}`} />
            ) : null}
            {convenioSelecionado.prazoTravaHoras != null ? (
              <RegraCard label="Trava regular" value={`${convenioSelecionado.prazoTravaHoras}h`} />
            ) : null}
            {convenioSelecionado.prazoPortabilidadeDU != null ? (
              <RegraCard label="Trava portabilidade" value={`${convenioSelecionado.prazoPortabilidadeDU} DU`} />
            ) : null}
            {convenioSelecionado.formatoImportacao ? (
              <RegraCard label="Importação" value={convenioSelecionado.formatoImportacao} />
            ) : null}
            {convenioSelecionado.vigenciaInicio ? (
              <RegraCard
                label="Vigência convênio"
                value={`${fmtDataISO(convenioSelecionado.vigenciaInicio)}${convenioSelecionado.vigenciaFim ? ` → ${fmtDataISO(convenioSelecionado.vigenciaFim)}` : " → aberta"}`}
              />
            ) : null}
          </div>
          {convenioSelecionado.vinculosAceitos.length > 0 ? (
            <div>
              <div style={{ fontSize: 11, letterSpacing: "0.06em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", marginBottom: 6 }}>
                Vínculos aceitos
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {convenioSelecionado.vinculosAceitos.map((v) => (
                  <Pill key={v} variant="averbado">{v}</Pill>
                ))}
              </div>
            </div>
          ) : null}
          {convenioSelecionado.regrasEspeciais ? (
            <div>
              <div style={{ fontSize: 11, letterSpacing: "0.06em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", marginBottom: 6 }}>
                Regras especiais
              </div>
              <div style={{
                background: "var(--bg-elev-2)", border: "1px solid var(--border)",
                borderRadius: 8, padding: "10px 12px", fontSize: 13, color: "var(--text)",
              }}>
                {convenioSelecionado.regrasEspeciais}
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {save.error ? (
        <div style={{ color: "var(--danger-500)", fontSize: 13, padding: 12, borderRadius: 8, background: "color-mix(in srgb, var(--danger-500) 8%, transparent)", border: "1px solid var(--danger-500)" }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            {save.error instanceof Error ? save.error.message : "Erro ao salvar"}
          </div>
          {(() => {
            // Backend retorna motivos especificos em `details` (ex.: {prazoMaxMeses:
            // "excede teto da prefeitura"}). Antes so aparecia "Dados invalidos"
            // generico — usuario nao sabia o que corrigir.
            if (!(save.error instanceof ApiHttpError) || !save.error.details) return null;
            const d = save.error.details as Record<string, string>;
            return (
              <ul style={{ margin: "6px 0 0 18px", padding: 0, fontSize: 12 }}>
                {Object.entries(d).map(([campo, motivo]) => (
                  <li key={campo}><b>{campo}:</b> {String(motivo)}</li>
                ))}
              </ul>
            );
          })()}
        </div>
      ) : null}

      <FormActions>
        <Button variant="ghost" type="button" onClick={() => nav("/banco/cadastros/tabela-emprestimos")}>Cancelar</Button>
        {/* As travas de vigencia sairam do `disabled`: com o campo derivado do
            convenio, nao ha como divergir — nao ha o que bloquear. */}
        <Button
          type="submit"
          disabled={save.isPending || taxaAcimaTeto || taxaAbaixoMinimo || faixaInvertida}
        >
          {save.isPending ? "Salvando..." : "Salvar"}
        </Button>
      </FormActions>
    </form>
  );
}

/** Card compacto readonly pro painel "Regras do convenio". */
function RegraCard({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        background: "var(--bg-elev-2)",
        border: "1px solid var(--border)",
        borderRadius: 8,
        padding: "8px 10px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <div style={{ fontSize: 10, letterSpacing: "0.06em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{value}</div>
    </div>
  );
}
